import React, { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import { useDispatch, useSelector } from "react-redux";

import BaseDataEntryDialog from "./BaseDataEntryDialog";
import {
  ChangeTimeType,
  CoordinateSystemChoice,
  CostSettings,
  OptimizationChoice,
} from "components/domain/settings";

import { selectRememberedSwitch, setRememberedSwitch } from "store/reducers/uiReducer.js";
import { selectCostSetting } from "store/reducers/selectors";


function TabPanel({ children, value, tabId }) {
  if (value !== tabId) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.string.isRequired,
  tabId: PropTypes.string.isRequired,
};

// Small banner clarifying where a group of settings takes effect. Global
// preferences follow the browser; the rest belong to the open scenario and
// travel with it (saved into scenario state, shared on export).
const ScopeNote = memo(({ scope }) => {
  const isDevice = scope === "device";
  const Icon = isDevice ? ComputerRoundedIcon : LayersRoundedIcon;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mb: 1.5,
        color: "text.secondary",
      }}
    >
      <Icon sx={{ fontSize: "0.95rem" }} />
      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
        {isDevice
          ? "Saved on this device — applies to every scenario."
          : "Applies to the current scenario and travels with it on export."}
      </Typography>
    </Box>
  );
});

ScopeNote.propTypes = {
  scope: PropTypes.oneOf(["device", "scenario"]).isRequired,
};

ScopeNote.displayName = "ScopeNote";

const SettingRow = memo(({ title, description, checked, onChange }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: 1.5,
      backgroundColor: "rgba(23, 43, 77, 0.02)",
    }}
  >
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={onChange}
          color="primary"
          size="small"
        />
      }
      label={
        <Box sx={{ ml: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 650, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
            {description}
          </Typography>
        </Box>
      }
      sx={{ alignItems: "flex-start", m: 0 }}
    />
  </Box>
));

SettingRow.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

SettingRow.displayName = "SettingRow";

const tabsSx = {
  minHeight: 44,
  borderBottom: "1px solid",
  borderColor: "divider",
  "& .MuiTab-root": {
    minHeight: 44,
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 2,
    px: 2,
    mr: 1,
  },
  "& .MuiTab-root.Mui-selected": {
    color: "primary.main",
    backgroundColor: "primary.extraLight",
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: 2,
  },
};

const SettingsDialog = memo(({ open, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("general");

  const rememberDownloadFormat = useSelector(selectRememberedSwitch("rememberDownloadFormat"));
  const ignoreExitExampleModeWarning = useSelector(
    selectRememberedSwitch("ignoreExitExampleModeWarning")
  );
  const costMetric = useSelector(selectCostSetting({ settingName: "costMetric" }));

  const handleToggleExportFormat = useCallback(
    (event) => {
      dispatch(
        setRememberedSwitch({ key: "rememberDownloadFormat", value: event.target.checked })
      );
    },
    [dispatch]
  );

  const handleToggleExitExampleMode = useCallback(
    (event) => {
      dispatch(
        setRememberedSwitch({
          key: "ignoreExitExampleModeWarning",
          value: event.target.checked,
        })
      );
    },
    [dispatch]
  );

  // Only distance/duration have a calculation method to configure; "custom"
  // means the user enters costs by hand, so there is nothing to show.
  const showCostMethod = costMetric === "distance" || costMetric === "duration";
  const costMethodLabel =
    costMetric === "duration" ? "Duration calculation method" : "Distance calculation method";

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={onClose}
      title="Settings"
      description="Preferences for this device and the current scenario, all in one place."
      maxWidth="md"
      showLogo={true}
      contentSx={{ px: 2.5, py: 2.25 }}
    >
      <Tabs
        value={activeTab}
        onChange={(_event, next) => setActiveTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={tabsSx}
      >
        <Tab value="general" label="General" />
        <Tab value="optimization" label="Optimization" />
        <Tab value="coordinates" label="Coordinates" />
        <Tab value="time" label="Time" />
      </Tabs>

      {/* Fixed-height, scrollable body so the dialog doesn't resize as the
          user moves between tabs (the Optimization tab is the tallest).
          Capped to the viewport so it never overflows on short screens. */}
      <Box sx={{ height: "min(440px, 58vh)", overflowY: "auto", overflowX: "hidden", pr: 0.5 }}>
      <TabPanel value={activeTab} tabId="general">
        <ScopeNote scope="device" />
        <Stack spacing={2}>
          <SettingRow
            title="Remember export format"
            description="Automatically use your last selected file format for downloads."
            checked={Boolean(rememberDownloadFormat)}
            onChange={handleToggleExportFormat}
          />
          <SettingRow
            title="Skip the example-mode exit warning"
            description="Leave example mode without the confirmation dialog each time."
            checked={Boolean(ignoreExitExampleModeWarning)}
            onChange={handleToggleExitExampleMode}
          />
        </Stack>
      </TabPanel>

      <TabPanel value={activeTab} tabId="optimization">
        <ScopeNote scope="scenario" />
        <OptimizationChoice />
        {showCostMethod && (
          <Box sx={{ mt: 2 }}>
            <CostSettings costMetric={costMetric} radioLabel={costMethodLabel} />
          </Box>
        )}
      </TabPanel>

      <TabPanel value={activeTab} tabId="coordinates">
        <ScopeNote scope="scenario" />
        <CoordinateSystemChoice />
      </TabPanel>

      <TabPanel value={activeTab} tabId="time">
        <ScopeNote scope="scenario" />
        <ChangeTimeType />
      </TabPanel>
      </Box>
    </BaseDataEntryDialog>
  );
});

SettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

SettingsDialog.displayName = "SettingsDialog";

export default SettingsDialog;
